import React, { FunctionComponent, useContext, useEffect, useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { useQuery } from '@apollo/react-hooks'
import { Helmet } from 'react-helmet'

import { AppContext, AppContextProps } from 'src/AppContext'
import { NotFound } from 'src/routes/NotFound'
import { AipModal } from 'src/components/aip-enabled-modal/aip-enabled-modal'
import { Attraction } from 'src/interfaces/Attraction'
import { asyncComponent } from 'src/lib/async-component'
import { trackAmplitude, GoogleAds } from 'src/lib/analytics'
import {
  getCategoryDigitalData,
  getEventAttributesDigitalData,
  googleTagManagerEvent,
  setDigitalData,
} from 'src/lib/digital-data'
import { LOCALES } from 'src/lib/locale'

import { EventPageLayout } from './EventPageLayout'

const attractionsQuery =
  require('src/components/calendar/AttractionsSection/attractions.gql')

import tiktokPixelInit from 'src/lib/tiktok-pixel'
import facebookPixelInit from 'src/lib/facebook-pixel'
import googleAnalyticsInit from 'src/lib/google-analytics'

const eventsQuery = require('./edp.gql')
const eventTimeSlotsQuery = require('./eventTimeSlots.gql')
const style = require('./Events.css')

const Navbar = asyncComponent(() => (
  import(/* webpackChunkName: "components-navbar", webpackPreload: true */ 'src/components/Navbar/Navbar')
), 'Navbar')

const Footer = asyncComponent(() => (
  import(/* webpackChunkName: "components-footer", webpackPrefetch: true */ 'src/components/Footer/Footer')
), 'Footer')

const Search = asyncComponent(() => (
  import(/* webpackChunkName: "components-search" */ 'src/components/search/index')
), 'Search')

const EventPage: FunctionComponent<RouteComponentProps> = ({ match, history }) => {
  useEffect(() => window.scrollTo(0, 0), [])

  const [searchShown, setSearchShown] = useState(false)
  const toggleSearch = () => setSearchShown(!searchShown)

  const [skipAllTimeSlotsFetch, setSkipAllTimeSlotsFetch] = useState(true)

  const { viewer, viewerFetched, switchLocale }: AppContextProps
    = useContext(AppContext)

  const { slug } = match.params

  const { data: eventData, loading: eventLoading, error: eventError } = useQuery(
    eventsQuery,
    { variables: { id: slug } },
  )

  const { data: attractionsData, loading: attractionsLoading, error: attractionsError } =
    useQuery<{ individualAttractions: Attraction[] }>(
      attractionsQuery,
      {
        variables: { listingId: slug },
      },
    )

  useEffect(() => {
    if (eventData && eventData.event?.timedEntry === false) {
      setSkipAllTimeSlotsFetch(false)
    }
  }, [eventData])

  const { data: timeSlotData } = useQuery(
    eventTimeSlotsQuery,
    { variables: { id: slug }, skip: skipAllTimeSlotsFetch },
  )

  setDigitalData({
    channel: 'event-display-page',
    type: 'Event Display Page',
    country: eventData?.event?.countryCode,
    category: getCategoryDigitalData(eventData?.event),
    attributes: getEventAttributesDigitalData(eventData?.event),
  })

  useEffect(() => {
    if (viewerFetched) {
      const { searchParams }: URL = new URL(window.location.href)

      trackAmplitude('EDP: View', { ref: searchParams.get('ref') }, viewer)
      googleTagManagerEvent({ event: 'virtualPageView' })
    }
  }, [viewer, viewerFetched])

  useEffect(() => {
    // Access ttq method in the iframe
    if (!eventData?.event?.tiktokPixelCodes || !eventData.event.tiktokPixelCodes.length) return
    tiktokPixelInit(eventData.event.tiktokPixelCodes)
  }, [eventData]);

  useEffect(() => {
    // Access fbq method in the iframe
    if (!eventData?.event?.facebookPixelCodes || !eventData.event.facebookPixelCodes.length) return
    facebookPixelInit(eventData.event.facebookPixelCodes)
  }, [eventData]);

  useEffect(() => {
    // Initialize Google Analytics
    if (!eventData?.event?.googleAnalytics4Id) return
    googleAnalyticsInit(eventData.event.googleAnalytics4Id)
  }, [eventData]);

  if (eventError) return <NotFound />

  if (!eventLoading && !eventError && eventData?.event?.googleAdsSendTos?.pageview.length > 0) {
    eventData.event.googleAdsSendTos.pageview.forEach((sendTo) => {
      GoogleAds.send(sendTo)
    })
  }

  if (searchShown) {
    return (
      <Search
        closeSearch={toggleSearch}
        lat={SSR_DATA.lat}
        lon={SSR_DATA.lon}
        locationQuery={SSR_DATA.cityName}
        viewer={viewer}
        page="edp"
        sectionId="search_bar"
        history={history}
      />
    )
  }

  const lnAipEnabled = eventData?.event?.lnAllInPricing

  const isEventFetched = !eventLoading && !eventError && !!eventData.event
  const isAttractionsFetched = !attractionsLoading && !attractionsError && !!attractionsData

  if (isEventFetched && isAttractionsFetched) {
    eventData.event.attractions = attractionsData.individualAttractions
  }

  return (
    <>
      <Helmet
        defaultTitle="Sell Tickets, Create Events and Discover Experiences - Universe"
      >
        <title>{`${eventData?.event?.title || 'Event Page'} - Universe`}</title>
        <meta name="description" content={`Buy Tickets for ${eventData?.event?.title} on Universe.com`} />
        <meta name="author" content={eventData?.event?.user?.name}></meta>
        <meta name="keywords" content={eventData?.event?.tags?.map((tag) => tag.name).join(', ')} />
      </Helmet>
      <div className={style.container}>
        {
          lnAipEnabled &&
            <AipModal eventSlug={slug} />
        }
        <Navbar
          transparent
          shadow
          className={style.navbar}
          toggleSearch={toggleSearch}
          showSearchBar
        />
        <EventPageLayout
          slug={slug}
          data={eventData}
          timeSlotData={timeSlotData}
          loading={eventLoading}
          viewer={viewer}
        />
        <Footer
          switchLocale={switchLocale}
          locales={LOCALES}
        />
      </div>
    </>
  )
}

export default EventPage
